import { Component, OnInit, OnDestroy, Inject } from '@angular/core';
import { debounceTime, map, Observable, of, Subscription, switchMap, tap, take } from 'rxjs';
import { DataInput, DataInputOption, InfoDialogComponent } from '@trivelope/triv-ui-tools-lib';
import { Environment } from 'src/app/classes/environment';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, FormGroup } from '@angular/forms';
import { CalendarService } from 'src/app/services/calendar.service';
import { CalendarGroup, EventCategory, PortalEvent } from 'src/app/classes/calendar';
import { formatDate } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Editor, toHTML, Toolbar } from 'ngx-editor';
import { MyFile } from 'src/app/classes/fileInfo';
import { MatDialog } from '@angular/material/dialog';
import { tmsEndAfterStartValidator } from './event-tmsEndBeforeStart.directive';
import { CalAndNewsFileService } from 'src/app/services/file.calendarAndNews.service';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { UserService } from 'src/app/services/user.service';
import { format } from 'date-fns';
import { CalendarTypeService } from 'src/app/services/calendar-type.service';
import { FileService } from 'src/app/services/file.service';
import { AvatarsService } from 'src/app/services/avatars.service';
import { EventUnsubscribeConfirmationDialog } from '../editors/event-unsubscribe-confirmation-dialog.component';


@Component({
	selector: 'app-event-editor',
	templateUrl: './event-editor.component.html',
	styleUrls: [ './event-editor.component.scss' ],
})
export class EventEditorComponent implements OnInit, OnDestroy {


	bCommInProgress = true;
	subscriptions = new Subscription();
	event = new PortalEvent();
	myInputs: { [name: string]: DataInput } = {};
	formG1: any = new FormGroup({}, { validators: tmsEndAfterStartValidator });
	formG2: any = new FormGroup({}, { validators: tmsEndAfterStartValidator });
	calendarGroups: CalendarGroup[] = [];
	eventCategories: EventCategory[] = [];

	editor = new Editor();
	toolbar: Toolbar = [
		['bold', 'italic', 'underline', 'strike'],
		// ['code', 'blockquote'],
		['ordered_list', 'bullet_list'],
		[{ heading: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] }],
		['link', 'image'],
		['text_color', 'background_color'],
		['align_left', 'align_center', 'align_right', 'align_justify'],
	];

  attachedFiles: MyFile[] = [];
  readonly FILESIZE_MAX_MB_TOTAL = 100;

	tmsLastSaveStart: number | null = null;
	tmsLastSaveEnd: number | null = null;

	bRegistered = false;

	bAdmin = this.authenticationService.isAdmin();
	usernameCache: { [key: string]: string } = {};
	selectedForm: 'formG1' | 'formG2' = 'formG1'; // Neue Variable zur Auswahl des Formulars
	mitgliedsnameCache: { [key: string]: string } = {};

  	bInternalCreation = false;


	yesUserIds: string[] = [];
	noUserIds: string[] = [];

	paramsEventId = "";
	paramsTms = "";


	constructor(
		@Inject('environment') private environment: Environment,
		private activatedRoute: ActivatedRoute,
		private calendarService: CalendarService,
		private matSnackBar: MatSnackBar,
		private router: Router,
		private matDialog: MatDialog,
		private calAndNewsFileService: CalAndNewsFileService,
		private authenticationService: AuthenticationService,
		private userService: UserService,
		private calendarTypeService: CalendarTypeService,
		private fileService: FileService,
		private avatarsService: AvatarsService
	) {
		this.formG1.addControl('ce_description', new FormControl());
		this.formG2.addControl('ce_description', new FormControl());

		this.formG1.addControl('nurIntern', new FormControl(false)); // immer false für Veranstaltungen
		this.formG2.addControl('nurIntern', new FormControl(true));  // immer true für Internen Kalender
	}


	ngOnInit() {

		this.userService.loadMitgliedsnamenCache().subscribe(cache => {
			this.mitgliedsnameCache = cache;
		});

		this.subscriptions.add(
			this.calendarTypeService.isInternalCalendar$.subscribe( isInternal => {
				this.bInternalCreation = isInternal;
				this.selectedForm = isInternal ? 'formG2' : 'formG1';  // Setze das richtige Formular basierend auf dem Kalendertyp
			})
		);

		for (let dataInput of this.environment.calendarEvent) {
			const di = new DataInput();
			di.id = dataInput.id;
			di.label = dataInput.label;
			di.type = dataInput.type;
			di.required = dataInput.required || false;
			di.options = dataInput.options || [];
			di.value = dataInput.value || '';
			di.groupOptions = dataInput.groupOptions || [];
			di.multipleFiles = dataInput.multipleFiles || false;
			di.title = dataInput.title || '';
			di.filesizeMaxMB = dataInput.filesizeMaxMB || 0;
			di.pattern = dataInput.pattern || '';
			di.placeholder = dataInput.placeholder || '';
			di.filetypesAccepted = dataInput.filetypesAccepted || [];

			this.myInputs[dataInput.id] = di;
		}

		const dt = new Date();
		const strToday = formatDate(dt.getTime(), 'yyyy-MM-dd', 'de'); // "2023-02-08";
		this.myInputs['ce_dateStart'].dateMin = strToday;
		this.myInputs['ce_dateEnd'].dateMin = strToday;
		dt.setFullYear(dt.getFullYear() + 100);
		const strFuture = formatDate(dt.getTime(), 'yyyy-MM-dd', 'de');
		this.myInputs['ce_dateStart'].dateMax = strFuture;
		this.myInputs['ce_dateEnd'].dateMax = strFuture;


		this.formG1.disable();
		this.formG2.disable();


		this.subscriptions.add(
			this.activatedRoute.queryParams.subscribe( params => {

				// Listener hinzufügen, um auf Änderungen im Betreff-Feld zu reagieren
				this.subscriptions.add(
					this.formG2.get('ce_name')?.valueChanges.subscribe( (value: string | null) => {
						if (value) { // Prüfen, ob der Betreff gesetzt wurde
							this.clickPublish();
						}
					})
				);

				this.paramsEventId = params['id'];
				this.paramsTms = params['tms'];

			})
		);

		this.calendarService.getCalendarGroupsAndCategories().subscribe( groupsAndCategories => {

			this.calendarGroups = groupsAndCategories.groups;

			const groupOptions: DataInputOption[] = [];
			for (const group of groupsAndCategories.groups) {
				groupOptions.push({ id: '' + group.vgId, label: group.vgName });
			}
			console.log( this.myInputs['ce_groups'].options, 'Gruppen optionen' );
			this.myInputs['ce_groups'].options = groupOptions;


			const categoryOptions: DataInputOption[] = [];
			for (const category of groupsAndCategories.categories) {
				categoryOptions.push({ id: '' + category.fkId, label: category.fkBezeichnung });
			}
			this.myInputs['ce_category'].options = categoryOptions;


			if( this.paramsEventId ) {

				this.bRegistered = this.calendarService.getSubscribedEventIds().includes( this.paramsEventId );

				this.calendarService.getEventFullInfo( this.paramsEventId ).subscribe( event => {

					this.bCommInProgress = false;
					if( !event ) return;

					this.event = event;
					this.sortAndFilterSubscribedIds();

					if( this.bAdmin && event.assignedUsers ) {
						this.userService.loadUsernameCache().subscribe(cache => {
							this.usernameCache = cache;
							this.sortAndFilterSubscribedIds();
						});
					}

					this.selectedForm = event.nurIntern ? 'formG2' : 'formG1';

					this.fillFormWithEventData(this.event); // Füllt das Formular mit Event-Daten

					if( this.selectedForm === 'formG2' ) {
						this.formG2.patchValue({ ce_place: 'intern' });
					}

					console.log( event.groups, 'Event groups from Backend' );
					const diSelectedOptions: DataInputOption[] = [];
					if (event.groups && event.groups.length) {
						for (const egId of event.groups) {
							const diOption = this.myInputs['ce_groups'].options?.find(o => o.id === egId);
							if (diOption) {
								diSelectedOptions.push(diOption); // Das gesamte DataInputOption-Objekt pushen
							}
						}
					}

					this.formG1.enable();
					this.formG2.enable();

					// setTimeout to avoid NG0100
					setTimeout(() => {

						this.formG1.patchValue({
							ce_place: event.place,
							ce_groups: diSelectedOptions[0],
							ce_name: event.name,
							ce_description: event.description
						});
						this.formG2.patchValue({
							ce_place: event.place,
							ce_name: event.name,
							ce_description: event.description
						});
						if (event.tmsStart !== null) {
							this.formG1.patchValue({
								ce_dateStart: formatDate(event.tmsStart, 'YYYY-MM-dd', 'de'),
								ce_timeStart: formatDate(event.tmsStart, 'HH:mm:ss', 'de'),
							});
							this.formG2.patchValue({
								ce_dateStart: formatDate(event.tmsStart, 'YYYY-MM-dd', 'de'),
								ce_timeStart: formatDate(event.tmsStart, 'HH:mm:ss', 'de'),
							});
						}
						if (event.tmsEnd !== null) {
							this.formG1.patchValue({
								ce_dateEnd: formatDate(event.tmsEnd, 'YYYY-MM-dd', 'de'),
								ce_timeEnd: formatDate(event.tmsEnd, 'HH:mm:ss', 'de'),
							});
							this.formG2.patchValue({
								ce_dateEnd: formatDate(event.tmsEnd, 'YYYY-MM-dd', 'de'),
								ce_timeEnd: formatDate(event.tmsEnd, 'HH:mm:ss', 'de'),
							});
						}
						if (event.tmsRegisterUntil !== null) {
							this.formG1.patchValue({
								ce_dateRegisterUntil: formatDate(event.tmsRegisterUntil, 'YYYY-MM-dd', 'de'),
								ce_timeRegisterUntil: '23:59',
							});
							this.formG2.patchValue({
								ce_dateRegisterUntil: formatDate(event.tmsRegisterUntil, 'YYYY-MM-dd', 'de'),
								ce_timeRegisterUntil: formatDate(event.tmsRegisterUntil, 'HH:mm:ss', 'de'),
							});
						}

						const reminderDateValue = event.tmsReminderMail !== null ? formatDate(event.tmsReminderMail, 'YYYY-MM-dd', 'de') : '';
						const reminderTimeValue = event.tmsReminderMail !== null ? formatDate(event.tmsReminderMail, 'HH:mm:ss', 'de') : '';
						this.formG1.patchValue({
							ce_dateReminderMail: reminderDateValue,
							ce_timeReminderMail: reminderTimeValue
						});
						this.formG2.patchValue({
							ce_dateReminderMail: reminderDateValue,
							ce_timeReminderMail: reminderTimeValue
						});

						if( event.categoryId ) {
							const diOption = this.myInputs['ce_category'].options?.find(o => o.id == event.categoryId);
							if (diOption) {
								this.formG1.patchValue({
									ce_category: diOption
								});

								if (event.bReleased) {
									this.formG1.get('ce_category')?.disable({ emitEvent: false });
								}
							} else {
								console.log( '!! eventCategory id not found', event.categoryId );
							}
						}

						this.attachedFiles = event.files.sort((a, b) => a.file.name.localeCompare(b.file.name));

						this.listenToChanges();

					}, 1);

				});

			}
			else {

				this.bCommInProgress = false;

				this.selectedForm = this.bInternalCreation ? 'formG2' : 'formG1';

				// Stelle sicher, dass das richtige Formular aktiviert ist und die Standardwerte festlegt
				const form = this.selectedForm === 'formG1' ? this.formG1 : this.formG2;
				form.reset(); // Setzt das Formular auf die Standardwerte zurück

				// Setze den Ort auf 'intern' für formG2
				if (this.selectedForm === 'formG2') {
					form.patchValue({ ce_place: 'intern' });
				}
				// Hier kannst du auch Standardwerte für neue Events setzen
				form.patchValue({
					ce_place: this.selectedForm === 'formG2' ? 'intern' : '',
					ce_name: '',
					ce_description: '',
					ce_dateStart: '',
					ce_timeStart: '',
					ce_dateEnd: '',
					ce_timeEnd: '',
					ce_dateRegisterUntil: '',
					ce_timeRegisterUntil: '',
					ce_dateReminderMail: '',
					ce_timeReminderMail: '',
					ce_category: null,
					ce_groups: []
				});

				if( this.paramsTms ) {

					const tms = +this.paramsTms || 0;
					const dt = new Date(tms);
					if( dt && !isNaN(dt.getTime()) ) {

						// setTimeout to avoid NG0100
						setTimeout(() => {
							this.formG1.patchValue({
								ce_dateStart: formatDate(dt.getTime(), 'YYYY-MM-dd', 'de'),
								ce_timeStart: formatDate(dt.getTime(), 'HH:mm:ss', 'de'),
								ce_dateEnd: formatDate(dt.getTime() + 24 * 3600 * 1000, 'YYYY-MM-dd', 'de'),
								ce_timeEnd: formatDate(dt.getTime() + 24 * 3600 * 1000, 'HH:mm:ss', 'de'),
							});

							this.formG2.patchValue({
								ce_dateStart: formatDate(dt.getTime(), 'YYYY-MM-dd', 'de'),
								ce_timeStart: formatDate(dt.getTime(), 'HH:mm', 'de'),
								ce_dateEnd: formatDate(dt.getTime() + 24 * 3600 * 999, 'YYYY-MM-dd', 'de'),
								ce_timeEnd: formatDate(dt.getTime() + 24 * 3600 * 1000, 'HH:mm:ss', 'de'),
							});
							this.listenToChanges();
						});

					}

					this.formG1.enable();
					this.formG2.enable();

				} else {
					this.formG1.enable();
					this.formG2.enable();
					this.listenToChanges();
				}

			}

		});

	}


	ngOnDestroy() {
		this.editor.destroy();
		this.subscriptions.unsubscribe();
	}


  getNameInputData(): DataInput {
    if (this.selectedForm === 'formG2') {
      return { ...this.myInputs.ce_name, label: 'Betreff' };
    }
    return this.myInputs.ce_name;
  }



	private fillFormWithEventData(event: PortalEvent) {

		const form = this.selectedForm === 'formG1' ? this.formG1 : this.formG2;

		form.patchValue({
			ce_place: this.selectedForm === 'formG2' ? 'intern' : event.place,
			ce_name: event.name,
			ce_description: event.description,
			ce_dateStart: event.tmsStart ? formatDate( event.tmsStart, 'yyyy-MM-dd', 'de' ) : '',
			ce_timeStart: event.tmsStart ? formatDate( event.tmsStart, 'HH:mm:ss', 'de' ) : '',
			ce_dateEnd: event.tmsEnd ? formatDate( event.tmsEnd, 'yyyy-MM-dd', 'de' ) : '',
			ce_timeEnd: event.tmsEnd ? formatDate( event.tmsEnd, 'HH:mm:ss', 'de' ) : '',
			ce_dateRegisterUntil: event.tmsRegisterUntil ? formatDate( event.tmsRegisterUntil, 'yyyy-MM-dd', 'de' ) : '',
			ce_timeRegisterUntil: event.tmsRegisterUntil ? formatDate( event.tmsRegisterUntil, 'HH:mm:ss', 'de' ) : '',
			ce_dateReminderMail: event.tmsReminderMail ? formatDate( event.tmsReminderMail, 'yyyy-MM-dd', 'de' ) : '',
			ce_timeReminderMail: event.tmsReminderMail ? formatDate( event.tmsReminderMail, 'HH:mm:ss', 'de' ) : '',
			ce_category: this.myInputs['ce_category'].options?.find(o => o.id == event.categoryId) || null
		});

		if (this.selectedForm === 'formG1') {
			const diSelectedOptions: DataInputOption[] = [];
			if (event.groups && event.groups.length) {
				for (const egId of event.groups) {
					const diOption = this.myInputs['ce_groups'].options?.find(o => o.id === egId);
					if (diOption) {
						diSelectedOptions.push(diOption);
					}
				}
			}
			form.patchValue({ ce_groups: diSelectedOptions });
		}

	}


  private listenToChanges() {

    this.subscriptions.add(
      this.formG1.valueChanges.pipe(
        debounceTime(500),
        switchMap(values => {
          console.log('VALUES CHANGED', values);
          return this.save(this.getEventFromForm('formG1'));
        })
      ).subscribe()
    );

    this.subscriptions.add(
      this.formG2.valueChanges.pipe(
        debounceTime(500),
        switchMap(values => {
          console.log('VALUES CHANGED', values);
          return this.save(this.getEventFromForm('formG2'));
        })
      ).subscribe()
    );

    /*
    const ctrlGroups = this.formG1.get( "ce_groups" );
    if( ctrlGroups ) {
      this.subscriptions.add(
        ctrlGroups.valueChanges.pipe(
          debounceTime( 400 ),
          switchMap( values => {
            console.log( "GROUPVALUES CHANGED", values );
            return this.assignCalendarGroups( values );
          })
        ).subscribe()
      );
    }
    */

  }

  clickPublish() {

    if (!this.formG1.valid && !this.formG2.valid) {
      return;
    }
    const previousReleaseState = this.event.bReleased;

	this.bCommInProgress = true;
    return this.calendarService.releaseEvent(this.event.id).pipe(
      tap(b => {

		this.bCommInProgress = false;
        if (!b) return;

        if (!previousReleaseState) {
          this.matSnackBar.open('Die Veranstaltung wurde freigegeben', 'Freigeben');
        } else {
          this.matSnackBar.open('Die Änderungen wurden gesendet', 'Freigeben');
        }

        this.event.bReleased = true;

        this.formG1.get('ce_category')?.disable({ emitEvent: false });

      })
    ).subscribe();

  }

  private save(event: PortalEvent): Observable<PortalEvent | null> {

    this.tmsLastSaveStart = (new Date()).getTime();

    // Setze den nurIntern-Wert basierend auf dem ausgewählten Formular
    if (this.selectedForm === 'formG2') {
      event.nurIntern = true;  // Setze nurIntern auf true für interne Veranstaltungen
    } else {
      event.nurIntern = false; // Setze nurIntern auf false für externe Veranstaltungen
    }

	this.bCommInProgress = true;
    return this.calendarService.saveEvent(event).pipe(
      tap( savedEvent => {

		this.bCommInProgress = false;
        if (savedEvent) {

          this.tmsLastSaveEnd = (new Date()).getTime();
          this.matSnackBar.open('Die Veranstaltung wurde gespeichert', 'Speichern');
          this.event = savedEvent;
          this.event.assignedUsers = event.assignedUsers;

        }

      })
    );

  }

  private getEventFromForm(formType: 'formG1' | 'formG2'): PortalEvent {

    const form = formType === 'formG1' ? this.formG1 : this.formG2;
    const event = { ...this.event } as PortalEvent;

    event.place = form.get('ce_place')?.value || (formType === 'formG2' ? 'intern' : '');

	const strDayStart = form.get('ce_dateStart')?.value;
    if( strDayStart !== null ) {
      event.tmsStart = this.convertStrToDate(
        '' + strDayStart,
        '' + form.get('ce_timeStart')?.value
      );
    }


    const strDayEnd = form.get('ce_dateEnd')?.value;
    if (strDayEnd !== null) {
      event.tmsEnd = this.convertStrToDate(
        '' + strDayEnd,
        '' + form.get('ce_timeEnd')?.value
      );
    }

    const strDayRegisterUntil = form.get('ce_dateRegisterUntil')?.value;
    if (strDayRegisterUntil !== null) {
      event.tmsRegisterUntil = this.convertStrToDate(
        '' + strDayRegisterUntil,
        '' + (form.get('ce_timeRegisterUntil')?.value || '23:59')
      );
    }

    const strReminderDate = form.get('ce_dateReminderMail')?.value;
    const strReminderTime = form.get('ce_timeReminderMail')?.value;
    if (strReminderDate) {
      event.tmsReminderMail = this.convertStrToDate(
        '' + strReminderDate,
        '' + (strReminderTime || '00:00:00')
      );
    } else {
      event.tmsReminderMail = null;
    }

    event.place = form.get('ce_place')?.value || '';

    event.nurIntern = formType === 'formG2';

    if (!event.nurIntern) {
      event.groups = [];
      let diOptions: DataInputOption[] = form.get('ce_groups')?.value || [];
      if (!Array.isArray(diOptions)) {
        diOptions = [diOptions];
      }
      if (diOptions && diOptions.length) {
        for (const o of diOptions) {
          const calendarGroup = this.calendarGroups.find(cg => cg.vgId == o.id);
          if (calendarGroup) {
            event.groups.push(calendarGroup.vgId);
          }
        }
      }
    } else {
      event.groups = [];
    }

    event.name = form.get('ce_name')?.value || '';
    event.description = '';
    const ctrlDesc = form.get('ce_description');
    if (ctrlDesc && ctrlDesc.value) {
      if (ctrlDesc.value['type'] && ctrlDesc.value['type'] == 'doc') {
        event.description = toHTML(ctrlDesc.value);
      } else {
        event.description = ctrlDesc.value;
      }
    }
    event.categoryId = '';
    if (formType === 'formG1') {
      const categCtrl = form.get('ce_category');
      if (categCtrl && categCtrl.value && categCtrl.value['id']) {
        event.categoryId = categCtrl.value['id'];
      }
    }

    event.place = formType === 'formG2' ? 'intern' : (form.get('ce_place')?.value || '');

    return event;
  }


	private convertStrToDate( strDay: string, strTime: string ): number {

		const dt = new Date();
		dt.setHours( 0, 0, 0, 0 );

		const arr = strDay.split('-');
		if( arr && arr.length && arr.length == 3 ) {

			dt.setFullYear( +arr[0], +arr[1] - 1, +arr[2] );

			if( strTime ) {
				const arr2 = strTime.split(':');
				if( arr2 && arr2.length && arr2.length >= 2 ) {
					const hours = +arr2[0];
					const minutes = +arr2[1];
					const seconds = arr2.length >= 3 ? +arr2[2] : 0;
					dt.setHours( hours, minutes, seconds, 0 );
				}
			}

		}
		return dt.getTime();

	}

  clickDelete() {

	this.bCommInProgress = true;
    this.calendarService.deleteEvent(this.event.id).subscribe(b => {
		this.bCommInProgress = false;
		if (b) {
			this.router.navigate(['Veranstaltungen']);
		}
    });

  }

  onDescriptionChange(html: string) {

    if (html != this.event.description) {
      this.formG1.markAsTouched();
    }

  }

	isTmsError(): boolean {

		const ctrlEnd = this.formG1.get( "ce_dateEnd" );
		if( !ctrlEnd ) return false;

		return this.formG1.errors?.[ 'tmsEndBeforeStart' ]
			&& (ctrlEnd.touched || ctrlEnd.value)
			&& (this.formG1.touched || this.formG1.dirty);

	}


  aFileWasChosen(file: File) {
    if (!file) {
      return;
    }
    this.checkAndUpload([file]);
  }

  filesWereChosen(files: File[]) {
    if (!files) {
      return;
    }
    this.checkAndUpload(files);
  }

  private checkAndUpload(newFiles: File[]) {

    let totalSize = 0;
    for (const attachedFile of this.attachedFiles) {

      totalSize += attachedFile.file.size;

      for (const newFile of newFiles) {
        if ((attachedFile.file.name == newFile.name) && (attachedFile.file.size == newFile.size)) {
          this.matDialog.open(InfoDialogComponent, {
            data: { title: 'Duplikat', message: 'Diese Datei ist bereits enthalten.' }
          });
          return;
        }
      }

    }

    for (const newFile of newFiles) {
      totalSize += newFile.size;
    }

    if (totalSize >= (this.FILESIZE_MAX_MB_TOTAL * 1024 * 1024)) {
      this.matDialog.open(InfoDialogComponent, {
        data: { title: '', message: 'Die Gesamtdateigröße ist auf ' + this.FILESIZE_MAX_MB_TOTAL + ' MB begrenzt' }
      });
      return;
    }

    const filesToUpload: MyFile[] = [];
    for (const newFile of newFiles) {

      const myFile = new MyFile();
      myFile.file = newFile;

      filesToUpload.push(myFile);
      this.attachedFiles.push(myFile);
    }

		this.bCommInProgress = true;
		this.calAndNewsFileService.uploadCalendarFiles(this.event.id, filesToUpload).subscribe( b => {
			this.bCommInProgress = false;
		});

  }

  deleteFile(myFile: MyFile) {

    if (!myFile.id) {

      this.matDialog.open(InfoDialogComponent, {
        data: { title: '', message: 'Die Datei wird noch hochgeladen' }
      });
      return;

    } else {

		this.bCommInProgress = true;
      this.calAndNewsFileService.deleteUploadedFile(myFile.id).subscribe(b => {
		this.bCommInProgress = false;
        if (b) {
          const nPos = this.attachedFiles.findIndex(f => f.id == myFile.id);
          if (nPos != -1) {
            this.attachedFiles.splice(nPos, 1);
          }
        } else {
          this.matDialog.open(InfoDialogComponent, {
            data: { title: 'Fehler', message: 'die Datei wurde nicht gelöscht' }
          });
        }
      });

    }

  }

  downloadFile(attachedFile: MyFile) {

    if (attachedFile.percentageUploaded != 100) {
      this.matDialog.open(InfoDialogComponent, {
        data: { title: '', message: 'Die Datei wird noch hochgeladen' }
      });
      return;
    }

    this.fileService.downloadFileById(attachedFile.id, attachedFile.file.name);

  }


	subscribeToEvent() {

		this.bCommInProgress = true;
		this.calendarService.subscribeToEvent( this.event.id ).subscribe( b => {

			this.bCommInProgress = false;
			if( !b ) return;
			this.bRegistered = true;
			const ebeId = this.authenticationService.getCurrentUser().ebeId;
			if( !this.event.assignedUsers.find( u => u.ebeId == ebeId) ) {
				this.event.assignedUsers.push( { ebeId: ebeId, vbAngemeldet: true } );
				this.sortAndFilterSubscribedIds();
			}

		});

	}

	private sortAndFilterSubscribedIds() {

		if( !this.event.assignedUsers || !this.event.assignedUsers.length ) {
			this.yesUserIds = [];
			this.noUserIds = [];
			return;
		}

		this.event.assignedUsers
			.map( user => ({
				userId: user.ebeId,
				mitgliedsname: this.mitgliedsnameCache[ user.ebeId ] || 'Unbekannt',
				username: this.usernameCache[ user.ebeId ] || 'Unbekannt'
			}))
			.sort( (a, b) => {

				const mitgliedComparison = a.mitgliedsname.localeCompare(b.mitgliedsname);
				if( mitgliedComparison !== 0 ) return mitgliedComparison;

				const nameComparison = a.username.localeCompare(b.username);
				if( nameComparison !== 0 ) return nameComparison;

				return a.userId.localeCompare(b.userId);

			});

		const yesUserIds = this.event.assignedUsers.filter( u => u.vbAngemeldet ).map( u => u.ebeId );
		this.yesUserIds = [ ...new Set(yesUserIds) ];	// to make sure there are no duplicates
		const noUserIds = this.event.assignedUsers.filter( u => !u.vbAngemeldet ).map( u => u.ebeId );
		this.noUserIds = [ ...new Set(noUserIds) ];	// to make sure there are no duplicates

		const uniqueIds = Array.from(new Set([...this.yesUserIds, ...this.noUserIds]));
		if (uniqueIds.length) {
			this.subscriptions.add(
				this.avatarsService.ensureMinis(uniqueIds).pipe(take(1)).subscribe()
			);
		}

	}


  isRegistrable(): boolean {

    if (this.event.tmsRegisterUntil === null) {
      return true;
    }

    const tmsNow = (new Date()).getTime();
    return tmsNow < this.event.tmsRegisterUntil;

  }

  seeRegistration(userId: string) {

    if( this.bCommInProgress ) return;

    this.router.navigate(['Veranstaltungen', 'anmeldung_übersicht'], { queryParams: { eventId: this.event.id, userId: userId } });

  }

  downloadFormAnswer() {
    const eventStartDate = new Date(Number(this.event.tmsStart));
    const eventStartFormattedValue = format(eventStartDate, 'dd.MM.yyyy');
    const eventEndDate = new Date(Number(this.event.tmsEnd));
    const eventEndFormattedValue = format(eventEndDate, 'dd.MM.yyyy');
    const eventName = this.event.name;
    const csvFileName = eventName + '_' + eventStartFormattedValue + '_' + eventEndFormattedValue + '.csv';
    this.calendarService.downloadFormAnswers(this.event.id, csvFileName);
  }

	clickKickUser( userId: string ) {

		this.matDialog.open( EventUnsubscribeConfirmationDialog ).afterClosed().subscribe( b => {

			if( !b ) return;
			if( this.bCommInProgress ) return;

			this.bCommInProgress = true;
			this.calendarService.unsubscribeUsersFromEvent( this.event.id, [ userId ] ).subscribe( b => {

				this.bCommInProgress = false;
				if( !b ) return;

				const nPos = this.event.assignedUsers.findIndex( u => u.ebeId == userId );
				if( nPos != -1 ) {
					this.event.assignedUsers.splice( nPos, 1 );
				}

				const ebeId = this.authenticationService.getCurrentUser().ebeId;
				if( userId == ebeId ) {
					this.bRegistered = false;
					this.calendarService.removeEventFromSubscribedEvents( this.event.id );
				}

				this.sortAndFilterSubscribedIds();

			});

		});

	}

  downloadICSFile() {
    this.calendarService.downloadICSFile(this.event.id);
  }

}

